import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { isAdmin } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { NavBarLink } from '../../../web/components/Happiness/NavBarLink';
import { styleSheet as navLinkStyleSheet } from '../../../web/components/Happiness/NavBarLink/styles';
import { styleSheet as navStyleSheet } from '../../../web/components/Happiness/NavBar/styles';
import { AlarmClockIcon } from '../../../web/components/svgs/icons/AlarmClockIcon';
import { ApproveMarkIcon } from '../../../web/components/svgs/icons/ApproveMarkIcon';
import { AssessmentReportIcon } from '../../../web/components/svgs/icons/AssessmentReportIcon';
import { CalendarDateIcon } from '../../../web/components/svgs/icons/CalendarDateIcon';
import { CallLogIcon } from '../../../web/components/svgs/icons/CallLogIcon';
import { FollowUpsIcon } from '../../../web/components/svgs/icons/FollowUpsIcon';
import { PhoneIcon } from '../../../web/components/svgs/icons/PhoneIcon';
import { SearchIcon } from '../../../web/components/svgs/icons/SearchIcon';
import { SettingsIcon } from '../../../web/components/svgs/icons/SettingsIcon';
import { useSearch } from '../../contexts/search';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import { isCoffeeMobileRoute, useIsCoffeeMobile } from '../../shell/CoffeeMobileRouteGuard';
import { white } from '../../styles/colors';
import { getTimeTrackingNavItems } from '../../timeTracking/navItems';
import { useTimeTracking } from '../../timeTracking/useTimeTracking';
import { CoffeeLogo } from '../svgs/CoffeeLogo';
import { TrophyIcon } from '../svgs/icons/TrophyIcon';
import { styleSheet } from './styles';

type CoffeeNavItem = {
	linkName: string;
	path: string | string[];
	icon: React.ReactNode;
	badge?: number;
	ariaLabel?: string;
};

const TIME_TRACKING_ICONS = {
	hoursReview: <ApproveMarkIcon color={white} height={18} width={19} />,
	timesheet: <AlarmClockIcon color={white} height={20} width={20} />,
};

const NAV_ITEMS: CoffeeNavItem[] = [
	{
		linkName: 'Queue',
		// `/queue` redirects to `/focused-view` for reps who opted in, so the link stays lit there too.
		path: ['/queue', '/focused-view'],
		icon: <PhoneIcon fill={white} />,
	},
	{
		linkName: 'Call Log',
		path: '/call-log',
		icon: <CallLogIcon fill={white} />,
	},
	{
		linkName: 'Demos',
		path: '/demos',
		icon: <CalendarDateIcon fillColor={white} />,
	},
	{
		linkName: 'Follow Ups',
		path: '/follow-ups',
		icon: <FollowUpsIcon fill={white} />,
	},
	{
		linkName: 'Leaderboard',
		path: '/leaderboard',
		icon: <TrophyIcon fillColor={white} />,
	},
];

const COACHING_REVIEW_ITEM: CoffeeNavItem = {
	linkName: 'Coaching Review',
	path: '/ai-coaching-review',
	icon: <AssessmentReportIcon fillColor={white} />,
};

const SETTINGS_ITEM: CoffeeNavItem = {
	linkName: 'Settings',
	path: '/settings',
	icon: <SettingsIcon stroke={white} />,
};

function _CoffeeNavBar({ compactLayout, onSearch }: { compactLayout: boolean; onSearch?: () => void }) {
	const userSession = useUserSession();
	const { setShowSearch } = useSearch();
	const isMobile = useIsCoffeeMobile();
	const { hasManagerPermissions } = useManagerPermissions();

	const timeTracking = useTimeTracking();
	const timeTrackingItems: CoffeeNavItem[] = getTimeTrackingNavItems(timeTracking).map(item => ({
		ariaLabel: item.ariaLabel,
		badge: item.badge,
		icon: TIME_TRACKING_ICONS[item.key],
		linkName: item.name,
		path: item.path,
	}));
	const allItems = [
		...NAV_ITEMS,
		...(hasManagerPermissions ? [COACHING_REVIEW_ITEM] : []),
		...timeTrackingItems,
		...(isAdmin(userSession.user) ? [SETTINGS_ITEM] : []),
	];
	// Filter by where the link goes (the first path), not where it stays lit: Queue goes to `/queue`, which
	// a phone can't show, even though `/focused-view` is allowed.
	const items = isMobile
		? allItems.filter(({ path }) => isCoffeeMobileRoute(Array.isArray(path) ? path[0] : path))
		: allItems;

	return (
		<nav
			className={`coffee-nav-bar ${css(navStyleSheet.nav, !compactLayout && navStyleSheet.navExpanded)}`}
			aria-label='Coffee navigation'
		>
			<div className={css(navStyleSheet.header)}>
				<CoffeeLogo className={css(compactLayout ? styleSheet.logoCompact : styleSheet.logo)} />
			</div>
			<div className={css(navStyleSheet.links)}>
				{items.map(({ linkName, path, icon, badge, ariaLabel }) => (
					<NavBarLink
						key={linkName}
						accessory={
							badge ? (
								<div
									aria-label={ariaLabel}
									className={css(
										compactLayout ? navStyleSheet.compactUnreadTextMessageCount : navStyleSheet.unreadTextMessageCount
									)}
								>
									{badge > 99 ? '99+' : badge}
								</div>
							) : null
						}
						activeLinkStyles={[navStyleSheet.linkActive, !compactLayout && navStyleSheet.expandedTabIndicator]}
						compactLayout={compactLayout}
						icon={icon}
						linkName={linkName}
						linkStyles={[navStyleSheet.link, compactLayout && navStyleSheet.linkCompact]}
						path={path}
					/>
				))}
				{/* Search opens the legacy modal rather than routing, so it cannot go through NavBarLink. */}
				{!isMobile && (
					<button
						type='button'
						aria-label='Search'
						className={css(navStyleSheet.link, compactLayout && navStyleSheet.linkCompact, styleSheet.searchButton)}
						onClick={onSearch ?? (() => setShowSearch(value => !value))}
						title='Search'
					>
						<div className={css(navLinkStyleSheet.icon)}>
							<SearchIcon fillColor={white} />
						</div>
						{!compactLayout && <span className={css(navLinkStyleSheet.linkName)}>Search</span>}
					</button>
				)}
			</div>
		</nav>
	);
}

export const CoffeeNavBar = observer(_CoffeeNavBar);
